// Re-exports Apollo's React hooks with one behavior change: a query that was
// denied by the server must not render data. Generated hooks import this
// module instead of @apollo/client (see codegen.yml), so all query call sites
// inherit it and new queries get it for free.
//
// Keep this file minimal — it is on the path of every query in the app.
import * as ApolloReactHooks from '@apollo/client';
import type {
  DocumentNode,
  LazyQueryHookOptions,
  LazyQueryResultTuple,
  NoInfer,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { isAuthorizationError } from './isAuthorizationError';

export * from '@apollo/client';

const suppressDeniedData = <TData, TVariables extends OperationVariables>(
  result: QueryResult<TData, TVariables>,
  errorPolicy: QueryHookOptions<TData, TVariables>['errorPolicy']
): QueryResult<TData, TVariables> => {
  // Under 'none' (the app default — the client sets no default errorPolicy;
  // a handful of screens opt into 'all'), Apollo spreads the *previous*
  // result forward next to the error: client-side stale data, possibly the
  // very thing the server just denied. That's what this branch blanks.
  //
  // Under 'all', `data` is the server's own response with denials already
  // nulled server-side — real, partial data worth keeping. Dashboard-style
  // screens with several root fields rely on rendering the fields that
  // weren't denied while one was.
  //
  // Known edge: an 'all' denial whose whole response is `data: null` is
  // never written to the cache (Apollo's shouldWriteResult needs truthy
  // data), so a later cache re-read could resurface a stale cached value.
  // That's not this wrapper's job to fix — the link-level eviction in
  // links.ts removes the denied root field as the error passes through the
  // link chain, before any re-read happens (see the regression test in
  // apolloHooks.test.tsx).
  if (errorPolicy === 'all') return result;
  if (!isAuthorizationError(result.error)) return result;
  return { ...result, data: undefined };
};

export function useQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<NoInfer<TData>, NoInfer<TVariables>>
): QueryResult<TData, TVariables> {
  const result = ApolloReactHooks.useQuery(query, options);
  return suppressDeniedData(result, options?.errorPolicy);
}

export function useLazyQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: LazyQueryHookOptions<NoInfer<TData>, NoInfer<TVariables>>
): LazyQueryResultTuple<TData, TVariables> {
  const [execute, result] = ApolloReactHooks.useLazyQuery(query, options);
  return [execute, suppressDeniedData(result, options?.errorPolicy)];
}
